/**
 * stammdatenValidation.ts — pure Validation für die Mandanten-Self-Erfassung
 * der Stammdaten (Modul 00).
 *
 * Bewusst ohne React- oder Supabase-Imports, damit die Tests in Vitest ohne
 * Browser-/DB-Stub laufen.
 *
 * Pflichtfelder (siehe Konzept-Dokument + Whitelist in Migration 0018):
 *   - clientFullName    (string, ≥ 2 Zeichen)
 *   - clientBirthdate   (ISO YYYY-MM-DD, nicht in der Zukunft)
 *   - clientAddress     (string, ≥ 5 Zeichen)
 *   - businessBranch    (string, ≥ 2 Zeichen)
 *   - businessLegalForm (string, aus Whitelist)
 *   - businessStartDate (ISO YYYY-MM-DD, nicht in der Zukunft)
 *   - businessDescription (string, 10–500 Zeichen)
 * Optional:
 *   - businessTaxId
 */

import { isPredefinedBranch } from './branchOptions';

export type StammdatenFieldKey =
  | 'clientFullName'
  | 'clientBirthdate'
  | 'clientAddress'
  | 'businessBranch'
  | 'businessLegalForm'
  | 'businessStartDate'
  | 'businessTaxId'
  | 'businessDescription';

export interface StammdatenDraft {
  readonly clientFullName: string;
  readonly clientBirthdate: string; // YYYY-MM-DD oder ''
  readonly clientAddress: string;
  readonly businessBranch: string;
  readonly businessLegalForm: string;
  readonly businessStartDate: string; // YYYY-MM-DD oder ''
  readonly businessTaxId: string;
  readonly businessDescription: string;
}

export type StammdatenError =
  | 'required'
  | 'too-short'
  | 'too-long'
  | 'future-date'
  | 'invalid-date';

export interface StammdatenFieldValidation {
  readonly ok: boolean;
  readonly error?: StammdatenError;
}

export interface StammdatenValidationResult {
  readonly isComplete: boolean;
  readonly fields: Readonly<Record<StammdatenFieldKey, StammdatenFieldValidation>>;
  readonly missingFields: ReadonlyArray<StammdatenFieldKey>;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const VALID_LEGAL_FORMS: ReadonlySet<string> = new Set([
  'Einzelunternehmen',
  'GbR',
  'UG',
  'GmbH',
  'PartG',
  'Andere',
]);

/**
 * Erstellt eine leere Draft (alle Strings ''). Verwendung in Form-Initial-State.
 */
export function emptyStammdatenDraft(): StammdatenDraft {
  return {
    clientFullName: '',
    clientBirthdate: '',
    clientAddress: '',
    businessBranch: '',
    businessLegalForm: '',
    businessStartDate: '',
    businessTaxId: '',
    businessDescription: '',
  };
}

/**
 * Validiert ein einzelnes Feld. Pure Function — Eingabe = String, Ausgabe =
 * { ok, error? }. Tests können jede Spalte einzeln durchexerzieren.
 *
 * `now` Parameter nur für Tests reinjizierbar; Production nutzt new Date().
 */
export function validateField(
  key: StammdatenFieldKey,
  value: string,
  now: () => Date = () => new Date(),
): StammdatenFieldValidation {
  const v = value.trim();

  switch (key) {
    case 'businessTaxId':
      // Optional — nie required. Längen-Cap als Sicherheitsnetz.
      if (v.length > 64) return { ok: false, error: 'too-long' };
      return { ok: true };

    case 'clientFullName':
      if (v.length === 0) return { ok: false, error: 'required' };
      if (v.length < 2) return { ok: false, error: 'too-short' };
      return { ok: true };

    case 'clientAddress':
      if (v.length === 0) return { ok: false, error: 'required' };
      if (v.length < 5) return { ok: false, error: 'too-short' };
      return { ok: true };

    case 'businessBranch': {
      if (v.length === 0) return { ok: false, error: 'required' };
      // Free-Text auch erlaubt (aus dem „Andere"-Pfad). Mindestens 2 Zeichen.
      if (v.length < 2) return { ok: false, error: 'too-short' };
      return { ok: true };
    }

    case 'businessLegalForm':
      if (v.length === 0) return { ok: false, error: 'required' };
      // Whitelist, damit der DB-Trigger nicht später mit cryptic constraint
      // schreit. Wir lassen aber jeden String aus der Whitelist durch — das
      // Dropdown bietet sie sowieso nur in dieser Form an.
      if (!VALID_LEGAL_FORMS.has(v)) {
        // Defensive: User hat Free-Text injiziert? Wir akzeptieren, weil
        // das Feld serverseitig kein Enum ist (TEXT in cases-Schema). Wir
        // markieren NUR zu kurze Eingaben als ungültig.
        if (v.length < 2) return { ok: false, error: 'too-short' };
      }
      return { ok: true };

    case 'clientBirthdate':
    case 'businessStartDate': {
      if (v.length === 0) return { ok: false, error: 'required' };
      if (!ISO_DATE_RE.test(v)) return { ok: false, error: 'invalid-date' };
      const d = new Date(`${v}T00:00:00Z`);
      if (Number.isNaN(d.getTime())) return { ok: false, error: 'invalid-date' };
      const today = now();
      // Vergleich auf Tagesebene UTC — wenn das eingegebene Datum NACH heute
      // liegt, blocken wir.
      const todayMidnight = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
      );
      if (d.getTime() > todayMidnight.getTime()) {
        return { ok: false, error: 'future-date' };
      }
      return { ok: true };
    }

    case 'businessDescription':
      if (v.length === 0) return { ok: false, error: 'required' };
      if (v.length < 10) return { ok: false, error: 'too-short' };
      if (v.length > 500) return { ok: false, error: 'too-long' };
      return { ok: true };
  }
}

/**
 * Validiert die komplette Draft. Liefert pro Feld das Validation-Resultat
 * UND ob die Stammdaten insgesamt vollständig (= „erfasst") sind.
 *
 * `isComplete` ignoriert das optionale `businessTaxId` und ist nur dann true,
 * wenn ALLE Pflichtfelder ok sind.
 */
export function validateStammdaten(
  d: StammdatenDraft,
  now: () => Date = () => new Date(),
): StammdatenValidationResult {
  const keys: ReadonlyArray<StammdatenFieldKey> = [
    'clientFullName',
    'clientBirthdate',
    'clientAddress',
    'businessBranch',
    'businessLegalForm',
    'businessStartDate',
    'businessTaxId',
    'businessDescription',
  ];
  const result: Record<StammdatenFieldKey, StammdatenFieldValidation> = {
    clientFullName: { ok: false },
    clientBirthdate: { ok: false },
    clientAddress: { ok: false },
    businessBranch: { ok: false },
    businessLegalForm: { ok: false },
    businessStartDate: { ok: false },
    businessTaxId: { ok: true },
    businessDescription: { ok: false },
  };
  const missing: StammdatenFieldKey[] = [];
  for (const k of keys) {
    const r = validateField(k, d[k], now);
    result[k] = r;
    if (!r.ok && k !== 'businessTaxId') {
      missing.push(k);
    }
  }
  return {
    isComplete: missing.length === 0,
    fields: result,
    missingFields: missing,
  };
}

/**
 * Helper für UI: ist die Stammdaten-Erfassung „grundsätzlich angefangen"?
 * (mindestens ein Pflichtfeld gefüllt). Dient dem Mandant-Home als
 * Status-Indikator.
 */
export function hasAnyStammdatenInput(d: StammdatenDraft): boolean {
  return (
    d.clientFullName.trim().length > 0 ||
    d.clientBirthdate.trim().length > 0 ||
    d.clientAddress.trim().length > 0 ||
    d.businessBranch.trim().length > 0 ||
    d.businessLegalForm.trim().length > 0 ||
    d.businessStartDate.trim().length > 0 ||
    d.businessDescription.trim().length > 0
  );
}

/**
 * Mappt eine CaseRow (Fragment mit den Stammdaten-Feldern) auf eine
 * UI-Draft. Null-Werte werden zu leeren Strings — die Form arbeitet
 * controlled.
 *
 * Wir akzeptieren nur Untermenge des CaseRow, damit der Helper auch in
 * Tests einfach aufrufbar ist (kein vollständiger CaseRow-Fixture).
 */
export interface CaseStammdatenSlice {
  readonly client_full_name: string | null;
  readonly client_birthdate: string | null;
  readonly client_address: string | null;
  readonly business_branch: string | null;
  readonly business_legal_form: string | null;
  readonly business_start_date: string | null;
  readonly business_tax_id: string | null;
  readonly business_description: string | null;
}

export function caseToDraft(c: CaseStammdatenSlice): StammdatenDraft {
  return {
    clientFullName: c.client_full_name ?? '',
    clientBirthdate: c.client_birthdate ?? '',
    clientAddress: c.client_address ?? '',
    businessBranch: c.business_branch ?? '',
    businessLegalForm: c.business_legal_form ?? '',
    businessStartDate: c.business_start_date ?? '',
    businessTaxId: c.business_tax_id ?? '',
    businessDescription: c.business_description ?? '',
  };
}

/**
 * Mappt eine UI-Draft zurück auf das DB-Update-Payload.
 *
 * Leere Strings werden zu null — die DB-Spalten sind nullable. Trim wird
 * auf alle String-Felder angewendet. Datumsfelder werden ohne Trim, aber
 * mit Empty→null konvertiert.
 */
export function draftToCaseUpdate(d: StammdatenDraft): {
  client_full_name: string | null;
  client_birthdate: string | null;
  client_address: string | null;
  business_branch: string | null;
  business_legal_form: string | null;
  business_start_date: string | null;
  business_tax_id: string | null;
  business_description: string | null;
} {
  const norm = (v: string): string | null => {
    const t = v.trim();
    return t.length === 0 ? null : t;
  };
  return {
    client_full_name: norm(d.clientFullName),
    client_birthdate: norm(d.clientBirthdate),
    client_address: norm(d.clientAddress),
    business_branch: norm(d.businessBranch),
    business_legal_form: norm(d.businessLegalForm),
    business_start_date: norm(d.businessStartDate),
    business_tax_id: norm(d.businessTaxId),
    business_description: norm(d.businessDescription),
  };
}

// Re-export für Konsumenten, die isPredefinedBranch direkt brauchen
export { isPredefinedBranch };
